import { applyCharacterCard, characterName } from "./apply-character.js";
const LANE_ORDER = { left: 0, center: 1, right: 2 };
const TEXT_MESSAGE_RE = /^\s*@text\s+(from|to)\s+([A-Za-z][\w-]*)\s*:\s?(.*)$/;
const CHARACTER_KINDS = new Set([
    "character.show",
    "character.bubble"
]);
function emptyState() {
    return { beatIndex: 0, beatTotal: 0, cardKind: "raw", characters: [] };
}
/**
 * Walk cards[0..untilIndex] (inclusive) and accumulate the StageState the cursor card produces.
 * Pure function — no React, no DOM. Output drives <Stage>'s presentational layer.
 *
 * `protagonist` is the MC's compiler-form slug (from a project CLAUDE.md, see
 * `protagonistFromClaudeMd`). It drives left/right portrait bucketing exactly
 * like the ls-preview player: protagonist → left, the most-recent other
 * character → right (max two on screen). When omitted/null, character.show
 * falls back to the legacy single-centered placement.
 */
export function computeStageState(cards, untilIndex, assets, assetUriMap, protagonist) {
    const state = emptyState();
    state.beatTotal = cards?.length ?? 0;
    if (!cards || cards.length === 0) {
        return state;
    }
    const upper = Math.min(Math.max(untilIndex, 0), cards.length - 1);
    state.beatIndex = upper;
    const uriMap = assetUriMap ?? {};
    const characters = new Map();
    let insertion = 0;
    for (let index = 0; index <= upper; index++) {
        const card = cards[index];
        if (!card) {
            continue;
        }
        state.cardKind = card.kind;
        insertion = applyCard(card, state, characters, assets, uriMap, insertion, protagonist ?? null);
    }
    state.characters = sortCharacters([...characters.values()]).map((working) => {
        const url = resolveCharacterUrl(uriMap, assets, working.id, working.look);
        return {
            hasImage: Boolean(url),
            imageUrl: url,
            assetPath: resolveCharacterPath(assets, working.id, working.look),
            lane: working.lane,
            look: working.look,
            name: working.name
        };
    });
    return state;
}
/** Distinct background and character files worth preloading in the monitor. */
export function collectEpisodeAssetPaths(cards, assets) {
    if (!cards || cards.length === 0)
        return [];
    const paths = new Set();
    for (const card of cards) {
        let path;
        if (card.kind === "bg") {
            path = resolveAssetPath(assets, "background", stringField(card, "bg"));
        }
        else if (card.kind === "character.show") {
            path = resolveCharacterPath(assets, stringField(card, "character"), stringField(card, "look"));
        }
        if (path)
            paths.add(path);
    }
    return [...paths];
}
function applyCard(card, state, characters, assets, uriMap, insertion, protagonist) {
    if (CHARACTER_KINDS.has(card.kind)) {
        return applyCharacterCard(card, state, characters, assets, insertion, protagonist);
    }
    switch (card.kind) {
        case "bg": {
            const name = stringField(card, "bg");
            const url = resolveAssetUrl(uriMap, assets, "background", name);
            state.bg = {
                hasImage: Boolean(url),
                imageUrl: url,
                assetPath: resolveAssetPath(assets, "background", name),
                name,
                transition: stringField(card, "transition") || undefined
            };
            // Per spec: bg change clears scene-scoped overlays.
            state.cg = undefined;
            state.bubble = undefined;
            state.dialogue = undefined;
            state.minigame = undefined;
            state.sfx = undefined;
            // Player parity (reduce.ts bg case): a bg swap also wipes every character
            // slot so the new scene starts empty. The map is the cursor's working set
            // and is projected into state.characters after the walk.
            characters.clear();
            return insertion;
        }
        case "cg": {
            const name = stringField(card, "cg");
            const url = resolveAssetUrl(uriMap, assets, "cg", name);
            state.cg = { hasImage: Boolean(url), imageUrl: url, assetPath: resolveAssetPath(assets, "cg", name), name };
            state.dialogue = { speaker: "CG", text: stringField(card, "content") || name };
            // CG takes over the scene — clear any lingering thought bubble overlay.
            state.bubble = undefined;
            state.minigame = undefined;
            return insertion;
        }
        case "minigame": {
            const name = stringField(card, "minigame");
            state.minigame = {
                name,
                description: stringField(card, "description") || undefined,
                gameUrl: resolveMinigameUrl(uriMap, assets, name)
            };
            state.bubble = undefined;
            state.dialogue = undefined;
            return insertion;
        }
        case "music": {
            state.music = {
                name: stringField(card, "track"),
                state: musicStateFor(stringField(card, "action") || "play")
            };
            return insertion;
        }
        case "sfx": {
            state.sfx = { name: stringField(card, "sound") };
            return insertion;
        }
        case "phone": {
            const lines = card.fields.messages;
            const messages = Array.isArray(lines)
                ? lines.filter((line) => typeof line === "string").map(parsePhoneMessage)
                : [];
            state.phone = { messages };
            return insertion;
        }
        case "dialogue":
        case "narration":
        case "monologue": {
            const speaker = stringField(card, "character") || (card.kind === "narration" ? "NARRATOR" : "YOU");
            const look = stringField(card, "look");
            state.dialogue = { look: look || undefined, speaker, text: stringField(card, "text") };
            state.bubble = undefined;
            state.minigame = undefined;
            // Sprites persist through spoken lines. The production player clears them
            // on background changes or explicit hides, not on narration/monologue.
            return insertion;
        }
        default:
            return insertion;
    }
}
function sortCharacters(values) {
    return [...values].sort((a, b) => LANE_ORDER[a.lane] - LANE_ORDER[b.lane] || a.insertion - b.insertion);
}
function musicStateFor(action) {
    // New spec: `@music <name>` plays (engine decides silence vs crossfade) and
    // `@music stop` stops (engine applies the fadeout).
    if (action === "stop") {
        return "fadeout";
    }
    return "playing";
}
function parsePhoneMessage(line) {
    const match = line.match(TEXT_MESSAGE_RE);
    if (match?.[1] && match[2]) {
        return {
            direction: match[1] === "from" ? "from" : "to",
            raw: line,
            speaker: match[2],
            text: match[3]?.trim() ?? ""
        };
    }
    return { direction: "from", raw: line, speaker: "", text: line.trim() };
}
function resolveAssetUrl(uriMap, assets, kind, name) {
    if (!name) {
        return undefined;
    }
    const direct = uriMap[`${kind}:${name}`];
    if (typeof direct === "string" && direct) {
        return direct;
    }
    const entry = findAssetEntry(assets, kind, name);
    return entry ? resolveFromEntry(uriMap, entry) : undefined;
}
function resolveMinigameUrl(uriMap, assets, name) {
    if (!name) {
        return undefined;
    }
    const direct = uriMap[`minigame:${name}`];
    if (typeof direct === "string" && direct) {
        return direct;
    }
    const entry = assets?.minigames.find((candidate) => same(candidate.name, name));
    return entry ? resolveFromEntry(uriMap, entry) : undefined;
}
function resolveAssetPath(assets, kind, name) {
    if (!name)
        return undefined;
    return findAssetEntry(assets, kind, name)?.path || undefined;
}
function resolveCharacterPath(assets, id, look) {
    if (!id || !look)
        return undefined;
    const character = assets?.characters.find((entry) => same(entry.id, id) || same(entry.name, id));
    return character?.looks.find((entry) => same(entry.name, look))?.path || undefined;
}
function resolveCharacterUrl(uriMap, assets, id, look) {
    if (!id || !look) {
        return undefined;
    }
    const keys = [
        `character:${id}:${look}`,
        `character:${id.toLowerCase()}:${look}`,
        `character:${id.toUpperCase()}:${look}`
    ];
    for (const key of keys) {
        const value = uriMap[key];
        if (typeof value === "string" && value) {
            return value;
        }
    }
    const character = assets?.characters.find((entry) => same(entry.id, id) || same(entry.name, id));
    const entry = character?.looks.find((entry_) => same(entry_.name, look));
    return entry ? resolveFromEntry(uriMap, entry) : undefined;
}
function resolveFromEntry(uriMap, entry) {
    for (const candidate of [entry.thumbnail, entry.path]) {
        if (!candidate) {
            continue;
        }
        const mapped = uriMap[candidate];
        if (typeof mapped === "string" && mapped) {
            return mapped;
        }
    }
    return entry.thumbnail || entry.path || undefined;
}
function findAssetEntry(assets, kind, name) {
    if (!assets) {
        return undefined;
    }
    const pool = kind === "background" ? assets.backgrounds : assets.cg;
    return pool.find((entry) => same(entry.name, name));
}
function stringField(card, key) {
    const value = card.fields[key];
    return typeof value === "string" ? value : "";
}
function same(left, right) {
    return String(left ?? "").toLowerCase() === String(right ?? "").toLowerCase();
}
// Re-export so external callers (and tests) can use characterName for display.
export { characterName };
